/**
 * HEARTCHIKO — THE CANDLE WALL
 * candle-wall.js  |  v1.0
 * ─────────────────────────────────────────────────────────────────
 * Sections
 *   1.  Tribute data  ......... sample candles (replace with API/Liquid)
 *   2.  Tag color map ......... ivory-toned badges per candle color
 *   3.  renderCandle() ........ builds candle + hover card HTML
 *   4.  Hover card logic ...... position:fixed placement via getBoundingClientRect
 *   5.  Mood filter tabs ...... click handler, applyFilter()
 *   6.  Color swatch filter ... click handler
 *   7.  Pet / date filter ..... <select> change handler
 *   8.  Modal — open/close .... openModal(), closeModal(), ESC key
 *   9.  Modal — color picker .. pickColor(), renderPreview()
 *  10.  Modal — live preview .. updatePreview(), countChars()
 *  11.  Scroll reveal ......... IntersectionObserver on .reveal
 *
 * HOW TO INCLUDE IN SHOPIFY
 * ─────────────────────────
 *  1. Upload this file to Assets → "candle-wall.js"
 *  2. At the bottom of the section template, add:
 *       {{ 'candle-wall.js' | asset_url | script_tag }}
 *     OR use a <script src="…" defer> tag.
 *  3. The script expects these IDs in the DOM:
 *       #wallGrid        — flex container for candle entries
 *       #moodNav         — wrapper for .mood-tab elements
 *       #swatchRow       — wrapper for .swatch elements
 *       #petFilter       — <select> for pet type
 *       #dateFilter      — <select> for date range (read-only UI; wire to API)
 *       #visibleCount    — <span> showing number of visible candles
 *       #modalOverlay    — the modal backdrop div
 *       #modal           — the modal card div
 *       #petName         — input in modal
 *       #tributeMsg      — textarea in modal
 *       #charCount       — <span> inside char counter
 *       #colorPicker     — container of .color-pick circles
 *       #previewFlame    — div for live candle preview
 *       #previewName     — preview name text
 *       #previewMsg      — preview message text
 * ─────────────────────────────────────────────────────────────────
 */

/* ================================================================
   1. TRIBUTE DATA
   Replace this array with a Shopify metaobject fetch or
   a Liquid {{ section.settings }} JSON blob in production.
================================================================ */
const tributes = [
  { name:"Luna",    pet:"dog",    color:"pink",   mood:"grateful",   msg:"She taught me what unconditional love truly means. Every morning she greeted me like I was her whole world.",  loc:"San Diego, CA",     tag:"Golden Soul",      ago:"2h ago"  },
  { name:"Max",     pet:"dog",    color:"violet", mood:"grieving",   msg:"Still can't believe you're gone. Fourteen years together and it still feels like yesterday, buddy.",            loc:"Austin, TX",        tag:"Still Missed",     ago:"5h ago"  },
  { name:"Buddy",   pet:"dog",    color:"blue",   mood:"peaceful",   msg:"My most loyal companion through every season. You never left my side, not even once.",                          loc:"Portland, OR",      tag:"Loyal Heart",      ago:"1d ago"  },
  { name:"Daisy",   pet:"dog",    color:"gold",   mood:"joyful",     msg:"You made every single day feel like a celebration. The best alarm anyone could ever ask for.",                   loc:"Denver, CO",        tag:"Joy Bringer",      ago:"3h ago"  },
  { name:"Coco",    pet:"cat",    color:"amber",  mood:"grateful",   msg:"Twelve years of warmth on my lap and purrs through every hard night. Forever my little heater.",                 loc:"Chicago, IL",       tag:"Soft Light",       ago:"8h ago"  },
  { name:"Bella",   pet:"dog",    color:"pink",   mood:"reflective", msg:"The most gentle soul I have ever known. She greeted every stranger as though they were an old friend.",          loc:"Nashville, TN",     tag:"Forever Grateful", ago:"2d ago"  },
  { name:"Oliver",  pet:"cat",    color:"violet", mood:"grieving",   msg:"Missing you every single morning. The house is so quiet without your little meows.",                             loc:"Seattle, WA",       tag:"Still Missed",     ago:"6h ago"  },
  { name:"Charlie", pet:"dog",    color:"blue",   mood:"hopeful",    msg:"Twelve years of trust. We understood each other without a single word.",                                         loc:"Boston, MA",        tag:"Always With Me",   ago:"4d ago"  },
  { name:"Rosie",   pet:"dog",    color:"rose",   mood:"reflective", msg:"You were my first dog and my greatest teacher. I did not know how much love a small heart could hold.",          loc:"Atlanta, GA",       tag:"Little Light",     ago:"3d ago"  },
  { name:"Mochi",   pet:"rabbit", color:"gold",   mood:"joyful",     msg:"The floppiest ears and the biggest personality. You thumped your way into every single heart.",                  loc:"Los Angeles, CA",   tag:"Joy Bringer",      ago:"7h ago"  },
  { name:"Shadow",  pet:"cat",    color:"violet", mood:"grieving",   msg:"You were my shadow in every sense. Always there. Always watching. Always mine.",                                loc:"Minneapolis, MN",   tag:"Still Missed",     ago:"1d ago"  },
  { name:"Sunny",   pet:"dog",    color:"gold",   mood:"hopeful",    msg:"Named you Sunny because you brought light wherever you went. That light never really went out.",                loc:"Miami, FL",         tag:"Golden Soul",      ago:"12h ago" },
  { name:"Ginger",  pet:"cat",    color:"amber",  mood:"grateful",   msg:"You picked me at the shelter. That was the best decision either of us ever made.",                              loc:"Phoenix, AZ",       tag:"Forever Grateful", ago:"5d ago"  },
  { name:"Willow",  pet:"cat",    color:"sage",   mood:"peaceful",   msg:"You always appeared when I needed quiet company. You would arrive, purr once, and that was enough.",            loc:"Eugene, OR",        tag:"Soft Light",       ago:"9h ago"  },
  { name:"Peanut",  pet:"dog",    color:"gold",   mood:"joyful",     msg:"For the tiniest dog I ever met, you left the biggest hole in our family. We still check for you at the door.", loc:"Richmond, VA",      tag:"Little Light",     ago:"4h ago"  },
  { name:"Pearl",   pet:"cat",    color:"pink",   mood:"grateful",   msg:"Eighteen years. Long enough to grow up with you. Not long enough to say goodbye properly.",                     loc:"Savannah, GA",      tag:"Golden Soul",      ago:"8d ago"  },
  { name:"Archie",  pet:"dog",    color:"blue",   mood:"hopeful",    msg:"You were my son's first best friend. Watching them grow up together was one of life's great gifts.",            loc:"Columbus, OH",      tag:"Always With Me",   ago:"2h ago"  },
  { name:"Nori",    pet:"bird",   color:"violet", mood:"reflective", msg:"You said my name every morning for eleven years. I still hear it sometimes and I smile.",                       loc:"San Francisco, CA", tag:"Still Missed",     ago:"11h ago" },
  { name:"Scout",   pet:"dog",    color:"amber",  mood:"grateful",   msg:"You walked every trail with me. Every mountain, every hard Tuesday. You were always up for it.",                loc:"Boulder, CO",       tag:"Loyal Heart",      ago:"3d ago"  },
  { name:"Maple",   pet:"cat",    color:"rose",   mood:"peaceful",   msg:"Autumn was your season and mine too now. Orange leaves always make me think of you curled in the window.",      loc:"Burlington, VT",    tag:"Soft Light",       ago:"7d ago"  },
  { name:"Biscuit", pet:"dog",    color:"gold",   mood:"joyful",     msg:"You were ridiculous and perfect. No one has ever been more excited about a tennis ball than you.",              loc:"Charlotte, NC",     tag:"Joy Bringer",      ago:"1h ago"  },
  { name:"Ember",   pet:"cat",    color:"amber",  mood:"hopeful",    msg:"You came into my life when I was broken. Something about your warmth put me back together.",                    loc:"Tucson, AZ",        tag:"Little Light",     ago:"6h ago"  },
  { name:"Theo",    pet:"dog",    color:"blue",   mood:"grieving",   msg:"A year has passed and I still reach for your leash in the morning. Old habits are the hardest to break.",       loc:"Madison, WI",       tag:"Still Missed",     ago:"13h ago" },
  { name:"Hazel",   pet:"rabbit", color:"sage",   mood:"peaceful",   msg:"The quietest creature and the gentlest presence. You made the whole house feel more peaceful.",                loc:"Portland, OR",      tag:"Soft Light",       ago:"2d ago"  },
];

/* ================================================================
   2. TAG COLOR MAP
   Ivory-toned badge backgrounds keyed by candle color.
   Matches the HeartChiko surface palette — no vivid colours.
================================================================ */
const tagColors = {
  pink:   "background:#F5E8EE;color:#8C4060;",
  violet: "background:#EDE8F5;color:#604090;",
  blue:   "background:#E8EEF5;color:#304880;",
  gold:   "background:#F5EED8;color:#806030;",
  amber:  "background:#F5EAE0;color:#804020;",
  sage:   "background:#E8F0E8;color:#386040;",
  rose:   "background:#F5E8EC;color:#804050;",
};

/* Candle wax heights vary by color for a natural, uneven look */
const heights = { pink:44, violet:58, blue:50, gold:42, amber:54, sage:48, rose:44 };

/* ================================================================
   3. renderCandle(tribute, index) → HTML string
   Flicker class cycles a / b / c so no two adjacent candles
   move in identical rhythm.
================================================================ */
function renderCandle(t, i) {
  const flickers = ['flk-a', 'flk-b', 'flk-c'];
  const fl = flickers[i % 3];
  const h  = heights[t.color] || 48;

  return `
    <div class="candle-entry ${fl}"
         data-mood="${t.mood}"
         data-color="${t.color}"
         data-pet="${t.pet}">

      <!-- Animated candle -->
      <div class="tribute-candle tc-${t.color}">
        <div class="tc-flame-wrap">
          <div class="tc-flame-glow"></div>
          <div class="tc-flame"></div>
          <div class="tc-flame-inner"></div>
        </div>
        <div class="tc-wick"></div>
        <div class="tc-wax" style="height:${h}px;"></div>
        <div class="tc-plate"></div>
      </div>

      <!-- Pet name + time label -->
      <div class="candle-label">${t.name}</div>
      <div class="candle-age">${t.ago}</div>

      <!-- Hover card — position:fixed, coordinates set by JS -->
      <div class="tribute-hover">
        <p class="th-name">${t.name}</p>
        <p class="th-msg">"${t.msg}"</p>
        <div class="th-meta">
          <span class="th-tag" style="${tagColors[t.color] || ''}">${t.tag}</span>
          <span class="th-loc">📍 ${t.loc}</span>
          <span class="th-time">${t.ago}</span>
        </div>
      </div>
    </div>`;
}

/* Render all tribute candles into #wallGrid on page load */
const grid = document.getElementById('wallGrid');
if (grid) {
  tributes.forEach((t, i) => grid.innerHTML += renderCandle(t, i));
}

/* ================================================================
   4. HOVER CARD — position:fixed coordinate placement
   ─────────────────────────────────────────────────────────────────
   WHY position:fixed?
   The wall section has overflow:hidden (Tailwind's overflow-hidden).
   position:absolute children get clipped by that. Fixed elements
   are anchored to the viewport, so they can never be clipped by
   any ancestor's overflow setting.

   ⚠️  BUG HISTORY — three issues fixed, documented here so they
       don't get accidentally re-introduced:

   BUG 1 — Wrong element for the hover lift (CRITICAL)
   ─────────────────────────────────────────────────────
   In CSS, any element with a `transform` property becomes a new
   containing block for position:fixed children. If you write:
       .candle-entry:hover { transform: translateY(-2px); }
   …the hover card (a DOM child of .candle-entry) stops being
   viewport-fixed and becomes parent-relative. getBoundingClientRect()
   returns viewport coordinates, but the card is now positioned in
   the parent's coordinate space — so every card appears at the wrong
   candle. Fix: use `top: -2px` instead. A relative offset does NOT
   create a new containing block. See candle-wall.css for the
   matching rule.

   BUG 2 — Touch-device capability check blocked desktop hover
   ─────────────────────────────────────────────────────────────
   The old pattern `'ontouchstart' in window || navigator.maxTouchPoints > 0`
   returns true on touchscreen laptops (Surface, MacBook with Touch ID,
   many Windows laptops) even when the user is holding a mouse. This
   caused every mouseenter handler to bail early with `if (isTouch) return`.
   Fix: track the *last-used input type* via a `usingTouch` flag instead
   of querying device capability once at hover time.

   BUG 3 — Brief show→measure→hide cycle corrupted the CSS animation
   ────────────────────────────────────────────────────────────────
   The old code set display:block, measured offsetHeight, then set
   display:none — all in the same synchronous frame. This fired and
   immediately cancelled the hc-fade-up animation. When the card was
   finally shown for real, the browser considered the animation already
   played and left it stuck at opacity:0. Fix: use a fixed height
   estimate (170px) and force an animation restart via
   `card.style.animation = 'none'` → reflow → `card.style.animation = ''`.

   Algorithm:
   a) Use a fixed 170px card height estimate (avoids the animation bug).
   b) Center card horizontally over the candle; clamp 8px from either
      viewport edge so it never bleeds off screen.
   c) Place ABOVE the candle if rect.top ≥ cardHeight + gap + 10px.
      Otherwise place BELOW (and toggle .below for arrow direction).
   d) Force animation restart so the fade-in plays every time.
   e) On touch devices, tap opens a bottom-sheet variant; tap outside
      or the close button dismisses it.
================================================================ */

/* Track actual last-used input — NOT device capability.
   navigator.maxTouchPoints > 0 is true on many desktop browsers,
   which would incorrectly block mouse hover. Instead we watch for
   real touch/mouse events. */
let usingTouch = false;
window.addEventListener('touchstart', () => { usingTouch = true;  }, { passive: true });
window.addEventListener('mousemove',  () => { usingTouch = false; }, { passive: true });

/**
 * positionCard(card, entry)
 * Places the hover card above or below the given candle entry using
 * viewport-relative coordinates from getBoundingClientRect().
 * Forces the CSS animation to restart so the fade-in plays every time.
 */
function positionCard(card, entry) {
  const cardH = 170;   /* fixed estimate — avoids the animate-then-cancel bug */
  const rect  = entry.getBoundingClientRect();
  const cardW = 230;
  const gap   = 10;

  /* ── Horizontal: centred over candle, clamped to viewport ── */
  let left = rect.left + rect.width / 2 - cardW / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - cardW - 8));

  /* ── Vertical: above when there's room, otherwise below ── */
  let top, below;
  if (rect.top >= cardH + gap + 10) {
    top   = rect.top - cardH - gap;
    below = false;
  } else {
    top   = rect.bottom + gap;
    below = true;
  }

  card.style.left = left + 'px';
  card.style.top  = top  + 'px';
  card.classList.toggle('below', below);

  /* Force the hc-fade-up animation to restart on every show */
  card.style.animation = 'none';
  card.style.display   = 'block';
  card.offsetHeight;              /* trigger reflow so browser registers the reset */
  card.style.animation = '';      /* release override → stylesheet animation plays fresh */
}

function closeAllCards() {
  document.querySelectorAll('.tribute-hover').forEach(c => c.style.display = 'none');
}

document.querySelectorAll('.candle-entry').forEach(entry => {
  const card = entry.querySelector('.tribute-hover');
  if (!card) return;

  /* ── Desktop: hover shows / hides the card ── */
  entry.addEventListener('mouseenter', function () {
    if (usingTouch) return;
    positionCard(card, this);
  });
  entry.addEventListener('mouseleave', function () {
    if (usingTouch) return;
    card.style.display = 'none';
  });

  /* ── Mobile: tap toggles the card; second tap on same candle closes it ── */
  entry.addEventListener('click', function (e) {
    if (!usingTouch) return;
    e.stopPropagation();
    const isOpen = card.style.display === 'block';
    closeAllCards();
    if (!isOpen) positionCard(card, this);
  });

  /* Close button inside card (mobile) */
  const closeBtn = card.querySelector('.th-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', e => {
      e.stopPropagation();
      card.style.display = 'none';
    });
  }
});

/* Tap anywhere outside a candle closes open cards on touch devices */
document.addEventListener('click', () => { if (usingTouch) closeAllCards(); });

/* ================================================================
   5. MOOD FILTER TABS
   Clicking a .mood-tab updates activeMood and re-runs applyFilter().
================================================================ */
let activeMood = 'all';

const moodNav = document.getElementById('moodNav');
if (moodNav) {
  moodNav.addEventListener('click', e => {
    const tab = e.target.closest('.mood-tab');
    if (!tab) return;
    activeMood = tab.dataset.mood;
    document.querySelectorAll('.mood-tab').forEach(t => t.classList.remove('active'));
    tab.classList.add('active');
    applyFilter();
  });
}

/* ================================================================
   6. COLOR SWATCH FILTER
================================================================ */
const swatchRow = document.getElementById('swatchRow');
if (swatchRow) {
  swatchRow.addEventListener('click', e => {
    const sw = e.target.closest('.swatch');
    if (!sw) return;
    document.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
    sw.classList.add('active');
    applyFilter();
  });
}

/* ================================================================
   7. PET TYPE + DATE FILTER
   Date range filter (Last 7 Days / Last 30 Days) is UI-only in
   this prototype. Wire to your Shopify API / metaobject query
   in production to actually filter by submission date.
================================================================ */
const petFilter = document.getElementById('petFilter');
if (petFilter) {
  petFilter.addEventListener('change', applyFilter);
}

/**
 * applyFilter()
 * Reads activeMood, the active swatch, and the pet <select>.
 * Hides candles that don't match all three criteria.
 * Updates #visibleCount.
 */
function applyFilter() {
  const col = document.querySelector('.swatch.active')?.dataset.col || 'all';
  const pet = document.getElementById('petFilter')?.value.toLowerCase() || '';
  let n = 0;

  document.querySelectorAll('.candle-entry').forEach(c => {
    const moodMatch  = activeMood === 'all'  || c.dataset.mood  === activeMood;
    const colorMatch = col === 'all'          || c.dataset.color === col;
    const petMatch   = !pet                   || c.dataset.pet   === pet;
    const ok = moodMatch && colorMatch && petMatch;

    c.classList.toggle('hidden', !ok);
    if (ok) n++;
  });

  const counter = document.getElementById('visibleCount');
  if (counter) counter.textContent = n;
}

/* ================================================================
   8. MODAL — OPEN / CLOSE
================================================================ */

/**
 * openModal()
 * Called by the "Light a Candle" button(s).
 * Locks body scroll, shows the modal, renders default preview.
 */
function openModal() {
  const overlay = document.getElementById('modalOverlay');
  if (!overlay) return;
  overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
  renderPreview('pink');   /* default candle color */
}

function closeModal() {
  const overlay = document.getElementById('modalOverlay');
  if (!overlay) return;
  overlay.classList.remove('open');
  document.body.style.overflow = '';
}

/**
 * handleOverlay(event)
 * Closes modal when the backdrop (not the modal card) is clicked.
 * Attach to the overlay div: onclick="handleOverlay(event)"
 */
function handleOverlay(e) {
  if (e.target === document.getElementById('modalOverlay')) closeModal();
}

/* ESC key closes modal */
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') closeModal();
});

/* ================================================================
   9. MODAL — CANDLE COLOR PICKER
================================================================ */
let chosenColor = 'pink';

/**
 * pickColor(el)
 * Called via onclick on each .color-pick circle.
 * Updates selection state and refreshes live preview.
 */
function pickColor(el) {
  document.querySelectorAll('.color-pick').forEach(c => c.classList.remove('on'));
  el.classList.add('on');
  chosenColor = el.dataset.color;
  renderPreview(chosenColor);
}

/**
 * renderPreview(color)
 * Injects a flickering tribute candle into the modal's preview area.
 */
function renderPreview(color) {
  const h      = heights[color] || 48;
  const target = document.getElementById('previewFlame');
  if (!target) return;

  target.innerHTML = `
    <div class="tribute-candle tc-${color} flk-a" style="margin-bottom:10px;">
      <div class="tc-flame-wrap">
        <div class="tc-flame-glow"></div>
        <div class="tc-flame"></div>
        <div class="tc-flame-inner"></div>
      </div>
      <div class="tc-wick"></div>
      <div class="tc-wax" style="height:${h}px;"></div>
      <div class="tc-plate"></div>
    </div>`;
}

/* ================================================================
  10. MODAL — LIVE PREVIEW
================================================================ */

/**
 * updatePreview()
 * Mirrors pet name and tribute message into the preview card.
 * Attach oninput to #petName and #tributeMsg.
 */
function updatePreview() {
  const nameEl = document.getElementById('petName');
  const msgEl  = document.getElementById('tributeMsg');
  const previewName = document.getElementById('previewName');
  const previewMsg  = document.getElementById('previewMsg');

  if (previewName) previewName.textContent = nameEl?.value || "Your pet's name";
  if (previewMsg)  previewMsg.textContent  = '"' + (msgEl?.value || "Your message will appear here…") + '"';
}

/**
 * countChars()
 * Updates the character counter badge.
 * Attach oninput to #tributeMsg alongside updatePreview().
 */
function countChars() {
  const msgEl   = document.getElementById('tributeMsg');
  const counter = document.getElementById('charCount');
  if (counter && msgEl) counter.textContent = msgEl.value.length;
}

/* ================================================================
  11. SCROLL REVEAL
  .reveal elements fade up when they enter the viewport.
  Threshold 0.12 = trigger when 12% of element is visible.
================================================================ */
const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
      }
    });
  },
  {
    threshold: 0.1
  }
);

document.querySelectorAll('.reveal').forEach(el => {
  observer.observe(el);
});


document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('input[required], textarea[required]').forEach(el => {

    el.addEventListener('invalid', (e) => {
      if (e.target.validity.valueMissing) {
        e.target.setCustomValidity(
          e.target.dataset.msgEmpty || 'Please share this — we need it to reach you.'
        );
      } else if (e.target.validity.typeMismatch) {
        e.target.setCustomValidity(
          e.target.dataset.msgInvalid || "That doesn't look quite right — could you check?"
        );
      }
    });

    el.addEventListener('input', (e) => {
      e.target.setCustomValidity('');
    });

  });
});